import type { HttpError } from '@packages/domain/constants/http';
import {
  trackResendOTPCode,
  trackVerifyOTPSuccessful,
  trackVerifyOTPWrong,
  trackViewOTPModal,
} from 'actions/tracker/tracker';
import { newNotification } from 'actions/ui/ui';
import { AUTOFOCUS_DELAY_MS } from 'constants/timers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import { getCurrentCountryCode, getPlatform } from 'selectors/ui/ui';
import type { OtpContext } from 'services/payex/otp/types';
import { OtpChannel } from 'services/payex/otp/types';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils/translations/useTranslations';
import { useCountdown } from './useCountdown';
import { useSendOtp } from './useSendOtp';
import { useVerifyOtp } from './useVerifyOtp';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const FIFTEEN_MIN_IN_MS = 15 * 60 * 1000;
const FOCUS_MAX_ATTEMPTS = 5;

let lastOtpSent: {
  email: string;
  phone?: string;
  context: string;
  channel: OtpChannel;
  timestamp: number;
} | null = null;

export const resetOtpCache = () => {
  lastOtpSent = null;
};

type OtpApiError = {
  type: string;
  message: string;
  attemptsRemaining?: number;
};

const parseOtpError = (error: HttpError): OtpApiError | null => {
  const errors = error.parsedBody?.errors as OtpApiError[] | undefined;
  return errors?.[0] ?? null;
};

type UseOtpFlowParams = {
  isOpen: boolean;
  email: string;
  phone?: string;
  senderName?: string;
  recipientName?: string;
  context: OtpContext;
  channel: OtpChannel;
  onVerified: () => void;
  onSendError?: () => void;
};

const useOtpFlow = ({
  isOpen,
  email,
  phone,
  senderName,
  recipientName,
  context,
  channel,
  onVerified,
  onSendError,
}: UseOtpFlowParams) => {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const sendOtp = useSendOtp();
  const verifyOtp = useVerifyOtp();

  const platform = useSelector(getPlatform);
  const recipientId = useSelector(getRecipientId);
  const countryFrom = useSelector(getCurrentCountryCode) as string;
  const countryTo = useSelector(
    (state: RootState) => state.entities?.recipient?.country,
  );

  const pinFieldRef = useRef<HTMLDivElement>(null);
  const lastFailedCodeRef = useRef<string | null>(null);

  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { countdown: resendCooldown, start: startResendCooldown } =
    useCountdown();

  const isLoading = sendOtp.isPending || verifyOtp.isPending;

  const buildSendOtpPayload = useCallback(() => {
    const payload = {
      email,
      ...(phone && { phone }),
      senderName,
      context,
      channel,
      platform,
      ...(recipientId && { portalCode: recipientId }),
      ...(countryFrom && { countryFrom }),
      ...(countryTo && { countryTo }),
    };
    if (context === 'payer_information' && recipientName) {
      return { ...payload, recipientName };
    }
    return payload;
  }, [
    email,
    phone,
    senderName,
    recipientName,
    context,
    channel,
    platform,
    recipientId,
    countryFrom,
    countryTo,
  ]);

  const focusFirstInput = useCallback(() => {
    const attemptFocus = (retriesLeft = FOCUS_MAX_ATTEMPTS) => {
      const firstInput =
        pinFieldRef.current?.querySelector<HTMLInputElement>('input');

      if (firstInput && document.activeElement !== firstInput) {
        firstInput.focus();

        if (document.activeElement !== firstInput && retriesLeft > 0) {
          setTimeout(() => attemptFocus(retriesLeft - 1), 100);
        }
      }
    };

    setTimeout(() => attemptFocus(), AUTOFOCUS_DELAY_MS);
  }, []);

  const resetState = useCallback(() => {
    setCode('');
    setErrorMessage('');
  }, []);

  const showSendFailedToast = useCallback(() => {
    dispatch(
      newNotification({
        message:
          channel === OtpChannel.Sms
            ? 'otp.sms.error.sendFailedToast'
            : 'otp.error.sendFailed',
        type: 'error',
      }),
    );
  }, [dispatch, channel]);

  const showVerifyFailedToast = useCallback(() => {
    dispatch(
      newNotification({
        message: 'otp.error.verifyFailed',
        type: 'error',
      }),
    );
  }, [dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only fire when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(trackViewOTPModal());
      resetState();
      lastFailedCodeRef.current = null;
      focusFirstInput();

      const now = Date.now();
      const shouldSkipSend =
        lastOtpSent &&
        lastOtpSent.email === email &&
        lastOtpSent.phone === phone &&
        lastOtpSent.context === context &&
        lastOtpSent.channel === channel &&
        now - lastOtpSent.timestamp < FIFTEEN_MIN_IN_MS;

      if (shouldSkipSend) {
        return;
      }

      sendOtp.mutate(buildSendOtpPayload(), {
        onSuccess: data => {
          if (data.alreadyVerified) {
            onVerified();
            return;
          }
          lastOtpSent = {
            email,
            phone,
            context,
            channel,
            timestamp: Date.now(),
          };
        },
        onError: (error: HttpError) => {
          lastOtpSent = null;
          if (error.status === 429) {
            dispatch(
              newNotification({
                message: 'otp.error.rateLimitExceeded',
                type: 'error',
              }),
            );
          }
          if (onSendError) {
            onSendError();
          } else {
            showSendFailedToast();
          }
        },
      });
    }
  }, [isOpen]);

  const handleVerify = () => {
    if (code.length !== OTP_LENGTH) {
      setErrorMessage(i18n.t('otp.error.required'));
      return;
    }

    setErrorMessage('');
    const recipient = channel === OtpChannel.Sms ? (phone ?? '') : email;
    verifyOtp.mutate(
      { recipient, code },
      {
        onSuccess: () => {
          dispatch(trackVerifyOTPSuccessful(channel));
          onVerified();
        },
        onError: (error: HttpError) => {
          const otpError = parseOtpError(error);
          if (!otpError) {
            showVerifyFailedToast();
            setCode('');
            focusFirstInput();
            return;
          }

          if (code !== lastFailedCodeRef.current) {
            dispatch(trackVerifyOTPWrong(channel));
            lastFailedCodeRef.current = code;
          }
          setErrorMessage(i18n.t('otp.error.invalid'));
          setCode('');
          focusFirstInput();
        },
      },
    );
  };

  const handleResend = () => {
    if (resendCooldown > 0) {
      return;
    }

    lastFailedCodeRef.current = null;
    resetState();
    startResendCooldown(RESEND_COOLDOWN_SECONDS);

    sendOtp.mutate(buildSendOtpPayload(), {
      onSuccess: () => {
        dispatch(trackResendOTPCode(channel));
        lastOtpSent = { email, phone, context, channel, timestamp: Date.now() };
      },
      onError: (error: HttpError) => {
        if (error.status === 429) {
          dispatch(
            newNotification({
              message: 'otp.error.rateLimitExceeded',
              type: 'error',
            }),
          );
        }
        if (onSendError) {
          onSendError();
        } else {
          showSendFailedToast();
        }
      },
    });
  };

  return {
    code,
    setCode,
    errorMessage,
    isLoading,
    resendCooldown,
    pinFieldRef,
    handleVerify,
    handleResend,
  };
};

export { useOtpFlow };
